(function(root, factory) {
  var api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.WallforgeLivePreview = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function(root) {
  'use strict';

  var SCHEMA = 'wallforge.live-preview.v1';
  var MAX_PIXELS = 4000000;
  var OUTPUT_MIME_TYPE = 'image/webp';
  var OUTPUT_QUALITY = 0.9;
  var instances = new WeakMap();

  function clean(value) {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  function normalizeBaseType(value) {
    var normalized = clean(value).toLowerCase();
    return normalized === 'poster' ? 'print' : normalized;
  }

  function normalizeFrame(value) {
    var normalized = clean(value).toLowerCase();
    return normalized === '' || normalized === 'plain' || normalized === 'unframed'
      ? 'none'
      : normalized;
  }

  function parseContract(text) {
    var contract;

    try {
      contract = typeof text === 'string' ? JSON.parse(text) : text;
    } catch (_error) {
      return null;
    }

    if (!contract || contract.schema !== SCHEMA || contract.enabled !== true) return null;
    if (!Array.isArray(contract.profiles) || contract.profiles.length === 0 || contract.profiles.length > 50) return null;

    var keys = new Set();
    var matchOwners = new Set();
    var valid = contract.profiles.every(function(profile) {
      if (!profile || !clean(profile.key) || keys.has(profile.key)) return false;
      keys.add(profile.key);

      var match = profile.match || {};
      var baseType = normalizeBaseType(match.baseType);
      var frame = normalizeFrame(match.frame);
      var matchKey = baseType + '|' + frame;
      if (['print', 'canvas'].indexOf(baseType) === -1) return false;
      if (['none', 'black', 'white'].indexOf(frame) === -1 || matchOwners.has(matchKey)) return false;
      matchOwners.add(matchKey);
      if (!Number.isInteger(profile.width) || !Number.isInteger(profile.height)) return false;
      if (profile.width <= 0 || profile.height <= 0 || profile.width * profile.height > MAX_PIXELS) return false;
      if (clean(profile.alt).length > 180) return false;

      var backgroundUrl = clean(profile.backgroundUrl);
      var overlayUrl = clean(profile.overlayUrl);
      var effect = clean(profile.effect);
      if (effect && effect !== 'canvas-shadow') return false;
      if (effect === 'canvas-shadow' && (backgroundUrl || overlayUrl)) return false;
      if (effect !== 'canvas-shadow' && !backgroundUrl && !overlayUrl) return false;
      if (backgroundUrl && !/^https:\/\//i.test(backgroundUrl)) return false;
      if (overlayUrl && !/^https:\/\//i.test(overlayUrl)) return false;

      var artwork = profile.artwork || {};
      var numbers = ['left', 'top', 'width', 'height'].map(function(key) { return Number(artwork[key]); });
      return numbers.every(Number.isFinite) && numbers[0] >= 0 && numbers[1] >= 0 &&
        numbers[2] > 0 && numbers[3] > 0 && numbers[0] + numbers[2] <= 1 &&
        numbers[1] + numbers[3] <= 1 && ['cover', 'contain'].indexOf(artwork.fit) !== -1;
    });

    return valid ? contract : null;
  }

  function resolveProfile(contract, semantic) {
    if (!contract || !Array.isArray(contract.profiles)) return null;
    var baseType = normalizeBaseType(semantic && semantic.baseType);
    var frame = normalizeFrame(semantic && semantic.frame);
    if (!baseType) return null;

    return contract.profiles.find(function(profile) {
      var match = profile.match || {};
      return normalizeBaseType(match.baseType) === baseType && normalizeFrame(match.frame) === frame;
    }) || null;
  }

  function coverSourceRect(imageWidth, imageHeight, targetWidth, targetHeight, fit) {
    var sourceRatio = imageWidth / imageHeight;
    var targetRatio = targetWidth / targetHeight;
    var sx = 0;
    var sy = 0;
    var sw = imageWidth;
    var sh = imageHeight;

    if (fit === 'cover') {
      if (sourceRatio > targetRatio) {
        sw = imageHeight * targetRatio;
        sx = (imageWidth - sw) / 2;
      } else {
        sh = imageWidth / targetRatio;
        sy = (imageHeight - sh) / 2;
      }
    }

    return { sx: sx, sy: sy, sw: sw, sh: sh };
  }

  function containDestination(imageWidth, imageHeight, left, top, width, height) {
    var scale = Math.min(width / imageWidth, height / imageHeight);
    var drawWidth = imageWidth * scale;
    var drawHeight = imageHeight * scale;
    return {
      left: left + (width - drawWidth) / 2,
      top: top + (height - drawHeight) / 2,
      width: drawWidth,
      height: drawHeight
    };
  }

  function drawCanvasShadow(context, rect, canvasWidth, canvasHeight) {
    var left = rect.left * canvasWidth;
    var top = rect.top * canvasHeight;
    var width = rect.width * canvasWidth;
    var height = rect.height * canvasHeight;
    var shortEdge = Math.min(width, height);

    context.save();
    context.shadowColor = 'rgba(0, 0, 0, 0.16)';
    context.shadowBlur = Math.max(8, shortEdge * 0.04);
    context.shadowOffsetX = 0;
    context.shadowOffsetY = Math.max(4, shortEdge * 0.018);
    context.fillStyle = '#fff';
    context.fillRect(left, top, width, height);
    context.restore();
  }

  function fetchImage(url, signal, dependencies) {
    var fetchFn = dependencies.fetch || root.fetch.bind(root);
    return fetchFn(url, { mode: 'cors', credentials: 'omit', signal: signal })
      .then(function(response) {
        if (!response.ok) throw new Error('image_fetch_failed');
        return response.blob();
      })
      .then(function(blob) {
        if (!/^image\//i.test(clean(blob.type))) throw new Error('image_content_type_invalid');

        if (dependencies.createImageBitmap) return dependencies.createImageBitmap(blob);
        if (root.createImageBitmap) return root.createImageBitmap(blob);

        return new Promise(function(resolve, reject) {
          var objectUrl = dependencies.createObjectURL(blob);
          var image = new root.Image();
          image.onload = function() {
            dependencies.revokeObjectURL(objectUrl);
            resolve(image);
          };
          image.onerror = function() {
            dependencies.revokeObjectURL(objectUrl);
            reject(new Error('image_decode_failed'));
          };
          image.src = objectUrl;
        });
      });
  }

  function canvasToBlob(canvas, mimeType, quality) {
    return new Promise(function(resolve, reject) {
      if (typeof canvas.toBlob !== 'function') {
        reject(new Error('canvas_encode_unavailable'));
        return;
      }

      canvas.toBlob(function(blob) {
        if (blob) resolve(blob);
        else reject(new Error('canvas_encode_failed'));
      }, mimeType, quality);
    });
  }

  function captureAttribute(node, name) {
    return node && node.hasAttribute(name) ? node.getAttribute(name) : null;
  }

  function restoreAttribute(node, name, value) {
    if (!node) return;
    if (value === null || value === undefined) node.removeAttribute(name);
    else node.setAttribute(name, value);
  }

  function findPreviewScrollTarget(rootElement) {
    return rootElement.querySelector('[data-product-images]') ||
      rootElement.querySelector('.product__photos') ||
      rootElement.querySelector('[data-product-single-media-group]') ||
      rootElement.querySelector('.product-slideshow');
  }

  function stickyHeaderOffset() {
    var document = root && root.document;
    if (!document) return 0;

    var header = document.querySelector('.site-header, [data-section-type="header"], #shopify-section-header');
    if (!header) return 0;

    var style = root.getComputedStyle ? root.getComputedStyle(header) : null;
    var position = style ? clean(style.position) : '';
    if (position !== 'fixed' && position !== 'sticky') return 0;

    var rect = header.getBoundingClientRect ? header.getBoundingClientRect() : null;
    return Math.max(0, rect && rect.height ? rect.height : header.offsetHeight || 0);
  }

  function previewTargetIsMostlyVisible(target, offset) {
    if (!target || !target.getBoundingClientRect) return true;

    var rect = target.getBoundingClientRect();
    var viewportHeight = root.innerHeight ||
      (root.document && root.document.documentElement && root.document.documentElement.clientHeight) ||
      0;
    if (!viewportHeight || !rect.height) return true;

    var topBoundary = offset + 12;
    var visibleTop = Math.max(topBoundary, rect.top);
    var visibleBottom = Math.min(viewportHeight, rect.bottom);
    var visibleHeight = Math.max(0, visibleBottom - visibleTop);
    var availableHeight = Math.max(0, viewportHeight - topBoundary);
    var requiredHeight = Math.min(rect.height, availableHeight) * 0.6;

    return rect.top >= topBoundary - 24 && visibleHeight >= requiredHeight;
  }

  function scrollToPreviewTarget(rootElement, dependencies) {
    var target = findPreviewScrollTarget(rootElement);
    if (!target || !target.getBoundingClientRect) return false;

    var offset = stickyHeaderOffset();
    if (previewTargetIsMostlyVisible(target, offset)) return false;

    var rect = target.getBoundingClientRect();
    var pageYOffset = root.pageYOffset ||
      (root.document && root.document.documentElement && root.document.documentElement.scrollTop) ||
      0;
    var motionQuery = dependencies.matchMedia
      ? dependencies.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    var prefersReducedMotion = motionQuery && motionQuery.matches;
    var top = Math.max(0, Math.round(rect.top + pageYOffset - offset - 12));

    dependencies.scrollTo({
      top: top,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
    return true;
  }

  function selectedGallery(rootElement) {
    var main = rootElement.querySelector('.product-main-slide.is-selected img') ||
      rootElement.querySelector('.product-main-slide.starting-slide img') ||
      rootElement.querySelector('.product-main-slide img');
    if (!main) return null;
    var slide = main.closest('.product-main-slide');
    var index = slide && slide.dataset ? slide.dataset.index : null;
    if (index === null || index === undefined) return null;

    return {
      index: String(index),
      main: main,
      wrapper: main.closest('.image-wrap'),
      thumbs: Array.prototype.slice.call(rootElement.querySelectorAll('.product__thumb-item[data-index="' + index + '"] img')),
      thumbLinks: Array.prototype.slice.call(rootElement.querySelectorAll('.product__thumb-item[data-index="' + index + '"] a[data-product-thumb]'))
    };
  }

  function snapshotGallery(gallery) {
    if (!gallery) return null;
    return {
      index: gallery.index,
      main: {
        src: captureAttribute(gallery.main, 'src'),
        srcset: captureAttribute(gallery.main, 'srcset'),
        width: captureAttribute(gallery.main, 'width'),
        height: captureAttribute(gallery.main, 'height'),
        alt: captureAttribute(gallery.main, 'alt'),
        photoswipeSrc: captureAttribute(gallery.main, 'data-photoswipe-src'),
        photoswipeWidth: captureAttribute(gallery.main, 'data-photoswipe-width'),
        photoswipeHeight: captureAttribute(gallery.main, 'data-photoswipe-height')
      },
      wrapperStyle: captureAttribute(gallery.wrapper, 'style'),
      thumbs: gallery.thumbs.map(function(thumb) {
        return { node: thumb, src: captureAttribute(thumb, 'src'), srcset: captureAttribute(thumb, 'srcset') };
      }),
      links: gallery.thumbLinks.map(function(link) {
        return { node: link, href: captureAttribute(link, 'href') };
      })
    };
  }

  function updateGallery(rootElement, snapshot, url, width, height, alt) {
    var slide = rootElement.querySelector('.product-main-slide[data-index="' + snapshot.index + '"]');
    var main = slide && slide.querySelector('img');
    if (!main) return false;
    var wrapper = main.closest('.image-wrap');
    var ratio = height / width * 100;

    main.setAttribute('src', url);
    main.setAttribute('srcset', '');
    main.setAttribute('width', String(width));
    main.setAttribute('height', String(height));
    main.setAttribute('alt', alt || 'Custom product preview');
    if (main.hasAttribute('data-photoswipe-src')) main.setAttribute('data-photoswipe-src', url);
    if (main.hasAttribute('data-photoswipe-width')) main.setAttribute('data-photoswipe-width', String(width));
    if (main.hasAttribute('data-photoswipe-height')) main.setAttribute('data-photoswipe-height', String(height));
    if (wrapper) {
      wrapper.style.height = '0px';
      wrapper.style.paddingBottom = ratio + '%';
    }

    rootElement.querySelectorAll('.product__thumb-item[data-index="' + snapshot.index + '"] img').forEach(function(thumb) {
      thumb.setAttribute('src', url);
      thumb.setAttribute('srcset', '');
    });
    rootElement.querySelectorAll('.product__thumb-item[data-index="' + snapshot.index + '"] a[data-product-thumb]').forEach(function(link) {
      link.setAttribute('href', url);
    });
    relayout(rootElement);
    return true;
  }

  function updateGallerySource(rootElement, snapshot, url, alt) {
    var slide = rootElement.querySelector('.product-main-slide[data-index="' + snapshot.index + '"]');
    var main = slide && slide.querySelector('img');
    if (!main) return false;

    main.setAttribute('src', url);
    main.setAttribute('srcset', '');
    main.setAttribute('alt', alt || 'Custom artwork preview');
    if (main.hasAttribute('data-photoswipe-src')) main.setAttribute('data-photoswipe-src', url);

    rootElement.querySelectorAll('.product__thumb-item[data-index="' + snapshot.index + '"] img').forEach(function(thumb) {
      thumb.setAttribute('src', url);
      thumb.setAttribute('srcset', '');
    });
    rootElement.querySelectorAll('.product__thumb-item[data-index="' + snapshot.index + '"] a[data-product-thumb]').forEach(function(link) {
      link.setAttribute('href', url);
    });
    relayout(rootElement);
    return true;
  }

  function restoreGallery(rootElement, snapshot) {
    if (!snapshot) return;
    var slide = rootElement.querySelector('.product-main-slide[data-index="' + snapshot.index + '"]');
    var main = slide && slide.querySelector('img');
    if (main) {
      restoreAttribute(main, 'src', snapshot.main.src);
      restoreAttribute(main, 'srcset', snapshot.main.srcset);
      restoreAttribute(main, 'width', snapshot.main.width);
      restoreAttribute(main, 'height', snapshot.main.height);
      restoreAttribute(main, 'alt', snapshot.main.alt);
      restoreAttribute(main, 'data-photoswipe-src', snapshot.main.photoswipeSrc);
      restoreAttribute(main, 'data-photoswipe-width', snapshot.main.photoswipeWidth);
      restoreAttribute(main, 'data-photoswipe-height', snapshot.main.photoswipeHeight);
      restoreAttribute(main.closest('.image-wrap'), 'style', snapshot.wrapperStyle);
    }
    snapshot.thumbs.forEach(function(item) {
      restoreAttribute(item.node, 'src', item.src);
      restoreAttribute(item.node, 'srcset', item.srcset);
    });
    snapshot.links.forEach(function(item) { restoreAttribute(item.node, 'href', item.href); });
    relayout(rootElement);
  }

  function relayout(rootElement) {
    var slideshow = rootElement.querySelector('.product-slideshow');
    if (!slideshow) return;
    var flickity = slideshow._flickity;
    if (!flickity && root.Flickity && typeof root.Flickity.data === 'function') {
      flickity = root.Flickity.data(slideshow);
    }
    if (flickity && typeof flickity.resize === 'function') flickity.resize();
    if (flickity && typeof flickity.reposition === 'function') flickity.reposition();
  }

  function createWallForgeLivePreview(rootElement, contract, suppliedDependencies) {
    if (!rootElement || !contract) return null;
    var dependencies = Object.assign({
      fetch: root.fetch ? root.fetch.bind(root) : null,
      createObjectURL: root.URL && root.URL.createObjectURL ? root.URL.createObjectURL.bind(root.URL) : null,
      revokeObjectURL: root.URL && root.URL.revokeObjectURL ? root.URL.revokeObjectURL.bind(root.URL) : function() {},
      createCanvas: function() { return rootElement.ownerDocument.createElement('canvas'); },
      scheduleBackground: function(callback) {
        if (typeof root.requestIdleCallback === 'function') {
          return root.requestIdleCallback(callback, { timeout: 500 });
        }
        return root.setTimeout(callback, 0);
      },
      cancelBackground: function(handle) {
        if (typeof root.cancelIdleCallback === 'function') root.cancelIdleCallback(handle);
        else root.clearTimeout(handle);
      },
      matchMedia: root.matchMedia ? root.matchMedia.bind(root) : null,
      scrollTo: function(options) {
        if (typeof root.scrollTo === 'function') root.scrollTo(options);
      }
    }, suppliedDependencies || {});
    if (!dependencies.fetch || !dependencies.createObjectURL) return null;

    var artworkUrl = '';
    var artworkImage = null;
    var artworkImagePromise = null;
    var currentVariant = null;
    var snapshot = null;
    var generation = 0;
    var generationAbortController = null;
    var shellImageCache = new Map();
    var previewCache = new Map();
    var profileJobs = new Map();
    var profileQueue = [];
    var activeProfileJob = null;
    var queueScheduled = false;
    var scheduledQueueHandle = null;
    var destroyed = false;
    var activeArtworkDetail = {};
    var scrolledPreviewKeys = new Set();

    function closeImage(image) {
      if (image && typeof image.close === 'function') image.close();
    }

    function releaseShellImages() {
      shellImageCache.forEach(function(pending) {
        Promise.resolve(pending).then(closeImage).catch(function() {});
      });
      shellImageCache.clear();
    }

    function revokePreviewCache() {
      previewCache.forEach(function(preview) {
        dependencies.revokeObjectURL(preview.url);
      });
      previewCache.clear();
    }

    function cancelScheduledQueue() {
      if (!queueScheduled) return;
      if (scheduledQueueHandle !== null && scheduledQueueHandle !== undefined) {
        dependencies.cancelBackground(scheduledQueueHandle);
      }
      queueScheduled = false;
      scheduledQueueHandle = null;
    }

    function releaseQueuedJobs() {
      cancelScheduledQueue();
      profileQueue.forEach(function(job) { job.resolve(null); });
      profileQueue = [];
      profileJobs.clear();
    }

    function clearGeneratedState() {
      generation += 1;
      if (generationAbortController) generationAbortController.abort();
      generationAbortController = null;
      releaseQueuedJobs();
      revokePreviewCache();
      releaseShellImages();
      closeImage(artworkImage);
      artworkImage = null;
      artworkImagePromise = null;
    }

    function loadShellImage(url, signal) {
      if (shellImageCache.has(url)) return shellImageCache.get(url);

      var pending = fetchImage(url, signal, dependencies).catch(function(error) {
        shellImageCache.delete(url);
        throw error;
      });
      shellImageCache.set(url, pending);
      return pending;
    }

    function renderedDetail(preview) {
      return Object.assign({}, activeArtworkDetail || {}, {
        imageUrl: clean(activeArtworkDetail && activeArtworkDetail.imageUrl) || artworkUrl,
        previewUrl: preview.url,
        renderedUrl: preview.url,
        width: preview.width || null,
        height: preview.height || null,
        alt: preview.alt || 'Custom product preview'
      });
    }

    function notifyPreviewRendered(preview) {
      if (!rootElement || typeof rootElement.dispatchEvent !== 'function' || typeof root.CustomEvent !== 'function') return;
      rootElement.dispatchEvent(new root.CustomEvent('wallforge:live-preview-rendered', {
        bubbles: true,
        detail: renderedDetail(preview)
      }));
    }

    function autoScrollKey(detail) {
      return clean(detail && detail.jobId) ||
        clean(detail && detail.statusToken) ||
        clean(detail && detail.generationToken);
    }

    function maybeScrollToRenderedPreview(detail, rendered) {
      var key = autoScrollKey(detail);
      if (!rendered || !key || (detail && detail.autoScroll === false)) return false;
      if (scrolledPreviewKeys.has(key)) return false;

      scrolledPreviewKeys.add(key);
      return scrollToPreviewTarget(rootElement, dependencies);
    }

    function renderRaw(url, image) {
      if (dependencies.displayPreview) {
        return dependencies.displayPreview({ url: url, width: image && (image.width || image.naturalWidth), height: image && (image.height || image.naturalHeight), alt: 'Custom artwork preview' });
      }
      var gallery = selectedGallery(rootElement);
      if (!snapshot && gallery) snapshot = snapshotGallery(gallery);
      if (!snapshot) return false;

      var width = image && (image.width || image.naturalWidth);
      var height = image && (image.height || image.naturalHeight);
      if (width > 0 && height > 0) {
        var rendered = updateGallery(rootElement, snapshot, url, width, height, 'Custom artwork preview');
        if (rendered) {
          notifyPreviewRendered({
            url: url,
            width: width,
            height: height,
            alt: 'Custom artwork preview'
          });
        }
        return rendered;
      }
      var sourceRendered = updateGallerySource(rootElement, snapshot, url, 'Custom artwork preview');
      if (sourceRendered) {
        notifyPreviewRendered({
          url: url,
          alt: 'Custom artwork preview'
        });
      }
      return sourceRendered;
    }

    function semanticFor(variant) {
      if (dependencies.semanticFor) return dependencies.semanticFor(variant);
      var semantics = root.WallforgeVariantSemantics;
      if (!semantics || typeof semantics.buildSemanticVariant !== 'function') return {};
      var selected = typeof semantics.readSelectedOptions === 'function'
        ? semantics.readSelectedOptions(rootElement)
        : null;
      return semantics.buildSemanticVariant(variant, selected);
    }

    function selectedProfile() {
      return resolveProfile(contract, semanticFor(currentVariant));
    }

    function previewKey(profile) {
      return artworkUrl + '|' + profile.key;
    }

    function displayPreview(preview) {
      if (preview && dependencies.displayPreview) return dependencies.displayPreview(preview);
      if (!preview || !snapshot) return false;
      var rendered = updateGallery(
        rootElement,
        snapshot,
        preview.url,
        preview.width,
        preview.height,
        preview.alt
      );
      if (rendered) notifyPreviewRendered(preview);
      return rendered;
    }

    function composeProfile(profile, jobGeneration, signal) {
      var canvas = dependencies.createCanvas();
      canvas.width = profile.width;
      canvas.height = profile.height;
      var context = canvas.getContext('2d');
      if (!context) return Promise.resolve(null);

      var layers = [];
      if (clean(profile.backgroundUrl)) {
        layers.push({ role: 'background', promise: loadShellImage(profile.backgroundUrl, signal) });
      }
      layers.push({ role: 'artwork', promise: artworkImagePromise, rect: profile.artwork, fit: profile.artwork.fit });
      if (clean(profile.overlayUrl)) {
        layers.push({ role: 'overlay', promise: loadShellImage(profile.overlayUrl, signal) });
      }
      if (clean(profile.effect) === 'canvas-shadow') {
        drawCanvasShadow(context, profile.artwork, canvas.width, canvas.height);
      }

      return Promise.all(layers.map(function(layer) { return layer.promise; }))
        .then(function(images) {
          if (jobGeneration !== generation || signal.aborted) return null;

          layers.forEach(function(layer, index) {
            var image = images[index];
            var imageWidth = image.width || image.naturalWidth;
            var imageHeight = image.height || image.naturalHeight;
            if (layer.role !== 'artwork') {
              context.drawImage(image, 0, 0, canvas.width, canvas.height);
              return;
            }

            var left = layer.rect.left * canvas.width;
            var top = layer.rect.top * canvas.height;
            var width = layer.rect.width * canvas.width;
            var height = layer.rect.height * canvas.height;
            context.save();
            context.beginPath();
            context.rect(left, top, width, height);
            context.clip();

            if (layer.fit === 'contain') {
              // Photographic shells contain sample art; cover it before fitting the full design.
              if (clean(profile.backgroundUrl)) {
                context.fillStyle = '#fff';
                context.fillRect(left, top, width, height);
              }
              var destination = containDestination(imageWidth, imageHeight, left, top, width, height);
              context.drawImage(image, destination.left, destination.top, destination.width, destination.height);
            } else {
              var source = coverSourceRect(imageWidth, imageHeight, width, height, 'cover');
              context.drawImage(image, source.sx, source.sy, source.sw, source.sh, left, top, width, height);
            }
            context.restore();
          });

          return canvasToBlob(canvas, OUTPUT_MIME_TYPE, OUTPUT_QUALITY);
        })
        .then(function(blob) {
          if (!blob || jobGeneration !== generation || signal.aborted) return null;
          return {
            url: dependencies.createObjectURL(blob),
            width: profile.width,
            height: profile.height,
            profileKey: profile.key,
            alt: clean(profile.alt) || 'Custom product preview'
          };
        });
    }

    function runNextProfileJob() {
      queueScheduled = false;
      scheduledQueueHandle = null;
      if (destroyed || activeProfileJob || profileQueue.length === 0) return;

      var job = profileQueue.shift();
      job.started = true;
      activeProfileJob = job;
      composeProfile(job.profile, job.generation, job.signal)
        .then(function(preview) {
          if (!preview || job.generation !== generation) return null;
          previewCache.set(job.key, preview);
          return preview;
        })
        .catch(function(error) {
          if (error && error.name === 'AbortError') return null;
          return null;
        })
        .then(function(preview) { job.resolve(preview); })
        .finally(function() {
          if (profileJobs.get(job.key) === job) profileJobs.delete(job.key);
          if (activeProfileJob === job) activeProfileJob = null;
          if (destroyed) return;
          if (job.generation !== generation) {
            pumpProfileQueue();
            return;
          }
          if (profileQueue.length === 0) releaseShellImages();
          else pumpProfileQueue();
        });
    }

    function pumpProfileQueue() {
      if (destroyed || activeProfileJob || queueScheduled || profileQueue.length === 0) return;
      if (profileQueue[0].priority) {
        runNextProfileJob();
        return;
      }
      queueScheduled = true;
      scheduledQueueHandle = dependencies.scheduleBackground(runNextProfileJob);
    }

    function ensureProfile(profile, prioritize) {
      var key = previewKey(profile);
      if (previewCache.has(key)) return Promise.resolve(previewCache.get(key));

      var existing = profileJobs.get(key);
      if (existing) {
        if (prioritize && !existing.started) {
          existing.priority = true;
          profileQueue = profileQueue.filter(function(job) { return job !== existing; });
          profileQueue.unshift(existing);
          cancelScheduledQueue();
          pumpProfileQueue();
        }
        return existing.promise;
      }

      var resolveJob;
      var promise = new Promise(function(resolve) { resolveJob = resolve; });
      var job = {
        key: key,
        profile: profile,
        generation: generation,
        signal: generationAbortController.signal,
        priority: !!prioritize,
        started: false,
        promise: promise,
        resolve: resolveJob
      };
      profileJobs.set(key, job);
      if (prioritize) profileQueue.unshift(job);
      else profileQueue.push(job);
      pumpProfileQueue();
      return promise;
    }

    function warmRemainingProfiles(activeKey) {
      contract.profiles.forEach(function(profile) {
        if (profile.key === activeKey) return;
        ensureProfile(profile, false);
      });
    }

    function render() {
      if (!artworkUrl || (!snapshot && !dependencies.displayPreview)) return Promise.resolve(false);
      var profile = selectedProfile();
      if (!profile) {
        return Promise.resolve(renderRaw(artworkUrl, artworkImage));
      }

      var key = previewKey(profile);
      if (previewCache.has(key)) {
        return Promise.resolve(displayPreview(previewCache.get(key)));
      }

      var renderGeneration = generation;
      if (dependencies.displayPreview) renderRaw(artworkUrl, artworkImage);
      return ensureProfile(profile, true).then(function(preview) {
        var current = selectedProfile();
        if (!preview || renderGeneration !== generation || !current || current.key !== profile.key) return false;
        return displayPreview(preview);
      });
    }

    function setArtwork(url, detail) {
      var nextUrl = clean(url);
      if (!nextUrl) return Promise.resolve(false);

      clearGeneratedState();
      artworkUrl = nextUrl;
      activeArtworkDetail = Object.assign({}, detail || {}, {
        imageUrl: clean(detail && detail.imageUrl) || nextUrl,
        previewUrl: clean(detail && detail.previewUrl) || nextUrl
      });
      generationAbortController = new AbortController();
      var jobGeneration = generation;
      var scrollDetail = activeArtworkDetail;
      var gallery = selectedGallery(rootElement);
      if (!snapshot && gallery) snapshot = snapshotGallery(gallery);
      renderRaw(artworkUrl, null);

      artworkImagePromise = fetchImage(artworkUrl, generationAbortController.signal, dependencies)
        .then(function(image) {
          if (jobGeneration !== generation) {
            closeImage(image);
            return null;
          }
          artworkImage = image;
          return image;
        });

      return artworkImagePromise
        .then(function(image) {
          if (!image || jobGeneration !== generation) return false;
          var profile = selectedProfile();
          var currentRender = render();
          warmRemainingProfiles(profile ? profile.key : '');
          return currentRender;
        })
        .then(function(rendered) {
          if (jobGeneration === generation) maybeScrollToRenderedPreview(scrollDetail, rendered);
          return rendered;
        })
        .catch(function(error) {
          if (error && error.name === 'AbortError') return false;
          if (jobGeneration === generation) renderRaw(artworkUrl, null);
          return false;
        });
    }

    function onArtwork(event) {
      var detail = event.detail || {};
      var url = clean(detail.previewUrl || detail.imageUrl);
      if (!url) return;
      event.preventDefault();
      setArtwork(url, detail);
    }

    function onReset(event) {
      event.preventDefault();
      clearGeneratedState();
      artworkUrl = '';
      currentVariant = null;
      restoreGallery(rootElement, snapshot);
      snapshot = null;
    }

    function onVariant(event) {
      currentVariant = event && event.detail ? event.detail.variant || null : null;
      if (artworkUrl) render();
    }

    rootElement.addEventListener('wallforge:live-preview-artwork', onArtwork);
    rootElement.addEventListener('wallforge:live-preview-reset', onReset);
    rootElement.addEventListener('variantChange', onVariant);

    var currentVariantNode = rootElement.querySelector('[data-current-variant-json]');
    if (currentVariantNode && currentVariantNode.value) {
      try { currentVariant = JSON.parse(currentVariantNode.value); } catch (_error) {}
    }

    return {
      setArtwork: setArtwork,
      render: render,
      warm: function() {
        if (!artworkUrl || !artworkImagePromise) return Promise.resolve([]);
        return artworkImagePromise.then(function(image) {
          if (!image) return [];
          return Promise.all(contract.profiles.map(function(profile) {
            return ensureProfile(profile, false);
          }));
        });
      },
      reset: function() { onReset({ preventDefault: function() {} }); },
      destroy: function() {
        destroyed = true;
        onReset({ preventDefault: function() {} });
        rootElement.removeEventListener('wallforge:live-preview-artwork', onArtwork);
        rootElement.removeEventListener('wallforge:live-preview-reset', onReset);
        rootElement.removeEventListener('variantChange', onVariant);
        instances.delete(rootElement);
      }
    };
  }

  function initialize(scope) {
    var searchRoot = scope && typeof scope.querySelectorAll === 'function' ? scope : root.document;
    if (!searchRoot) return [];
    var scripts = Array.prototype.slice.call(searchRoot.querySelectorAll('[data-wallforge-live-preview-config]'));
    if (searchRoot.matches && searchRoot.matches('[data-wallforge-live-preview-config]')) scripts.unshift(searchRoot);

    return scripts.map(function(script) {
      var section = script.closest('[data-section-type="product"]');
      if (!section || instances.has(section)) return instances.get(section) || null;
      var contract = parseContract(script.textContent || '');
      if (!contract) return null;
      var instance = createWallForgeLivePreview(section, contract);
      if (instance) instances.set(section, instance);
      return instance;
    }).filter(Boolean);
  }

  if (root && root.document && typeof module === 'undefined') {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function() { initialize(root.document); });
    } else {
      initialize(root.document);
    }
    root.document.addEventListener('shopify:section:load', function(event) { initialize(event.target); });
    root.document.addEventListener('shopify:section:unload', function(event) {
      var section = event.target && event.target.closest ? event.target.closest('[data-section-type="product"]') : null;
      var instance = section && instances.get(section);
      if (instance) instance.destroy();
    });
  }

  return {
    SCHEMA: SCHEMA,
    parseContract: parseContract,
    resolveProfile: resolveProfile,
    coverSourceRect: coverSourceRect,
    containDestination: containDestination,
    drawCanvasShadow: drawCanvasShadow,
    createWallForgeLivePreview: createWallForgeLivePreview,
    initialize: initialize,
    updateGallery: updateGallery,
    restoreGallery: restoreGallery
  };
});
